import type { FC } from "react";
import React, { memo, useCallback, useMemo, useRef, useState } from "react";

import type { QuestionChildren } from "../../../types/main";
import { isDropzone } from "../../../utils/isDropzone";

type Props = {
    child: QuestionChildren;
    onClick?: (text: string) => void;
    onDrop: (data: { answer: string; child: QuestionChildren }) => void;
    text: string;
};

const ExerciseOrangesAnswerFC: FC<Props> = ({ child, onClick, onDrop, text }: Props) => {
    const ref = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState({});
    const style = useMemo(
        () => ({
            ...{
                left: 0,
                top: 0,
            },
            ...position,
        }),
        [position]
    );
    const onDrag = useCallback(
        (e) =>
            setPosition({
                height: 0,
                left: e.clientX,
                opacity: 0,
                position: "fixed",
                top: e.clientY,
                transform: "none",
                width: 0,
            }),
        []
    );
    const onDragEnd = useCallback((e) => setPosition({}), []);
    const onDragStart = useCallback(
        (e) => {
            e.dataTransfer.setData("text", text);
        },
        [text]
    );

    const onTouchMove = useCallback((e) => {
        const { clientX, clientY } = e.touches[0];
        (window as any).dropzone = isDropzone(clientX, clientY, "oranges_word_answer")
            ? "oranges_word_answer"
            : undefined;

        if (ref.current) {
            setPosition({
                height: ref.current.clientHeight,
                left: clientX - ref.current.clientWidth / 2,
                opacity: 1,
                position: "fixed",
                top: clientY - ref.current.clientHeight / 2,
                transform: "none",
                width: ref.current.clientWidth,
            });
        }
    }, []);
    const onTouchEnd = useCallback(() => {
        const dropzone = (window as any).dropzone as string | undefined;
        if (dropzone) {
            onDrop({
                answer: text,
                child,
            });
        }
        (window as any).dropzone = undefined;

        setPosition({});
    }, [child, onDrop]);

    const handleClick = useCallback(() => onClick?.(text), [onClick, text]);

    return (
        <div className="exercise-oranges__answer">
            {text === "" ? <>&nbsp;</> : text}
            <div
                className="exercise-oranges__answer-draggable"
                draggable
                onClick={handleClick}
                onDrag={onDrag}
                onDragEnd={onDragEnd}
                onDragStart={onDragStart}
                onTouchEnd={onTouchEnd}
                onTouchMove={onTouchMove}
                ref={ref}
                style={style}
            />
        </div>
    );
};

export const ExerciseOrangesAnswer = memo(ExerciseOrangesAnswerFC);
